import { TRACK_GRID_PX } from '@runkr/config'
import { bodiesOverlap, spansOverlap } from '../geom'
import { isPlatformSolid, platformPosAt } from '../terrain/platforms'
import { rampSupportY } from '../terrain/ramps'
import type { Aabb, Zone } from '../track/types'
import type { World } from '../world'
import type { Scene } from './scene'

// One forecast body against the terrain (`rollout.ts`): the same rules as `player/collide.ts`,
// `terrain/*` and `items/step.ts`, over a scene's entries.

export interface Sim {
  x: number
  y: number
  w: number
  h: number
  vy: number
}

/** An edge is identified by its grid cell: the first airborne x is within one step past it. */
export function edgeKey(edgeX: number): number {
  return Math.floor(edgeX / TRACK_GRID_PX)
}

export function inZone(world: World, scene: Scene, b: Sim, kind: Zone['kind']): boolean {
  for (const k of scene.zones) {
    const z = world.track.zones[k]!
    if (z.kind === kind && spansOverlap(b.x, b.w, z.x, z.w) && b.y >= z.y && b.y <= z.y + z.h) {
      return true
    }
  }
  return false
}

/** Carry a grounded body standing on a platform's top at `t` by its movement to `t + 1`. */
export function ride(world: World, scene: Scene, b: Sim, t: number): void {
  for (const k of scene.platforms) {
    const pl = world.track.platforms[k]!
    const st = world.platformState[k]!
    const prev = platformPosAt(pl, t)
    if (b.y === prev.y && spansOverlap(b.x, b.w, prev.x, pl.w) && isPlatformSolid(st, t)) {
      const next = platformPosAt(pl, t + 1)
      b.x += next.x - prev.x
      b.y += next.y - prev.y
      return
    }
  }
}

/** One-way landing on a platform top crossed between `t` and `t + 1`; highest wins. */
export function landPlatform(
  world: World,
  scene: Scene,
  b: Sim,
  prevFoot: number,
  t: number,
): boolean {
  if (b.vy < 0) return false
  let best = -1
  let bestTop = 0
  for (const k of scene.platforms) {
    const pl = world.track.platforms[k]!
    if (!isPlatformSolid(world.platformState[k]!, t)) continue
    const prevTop = platformPosAt(pl, t).y
    const next = platformPosAt(pl, t + 1)
    if (prevFoot > prevTop || b.y < next.y || !spansOverlap(b.x, b.w, next.x, pl.w)) continue
    if (best === -1 || next.y < bestTop) {
      best = k
      bestTop = next.y
    }
  }
  if (best !== -1) b.y = bestTop
  return best !== -1
}

/** `snapToRamps` over the scene's ramps; true when the body ends on a ramp. */
export function snapRamps(world: World, scene: Scene, b: Sim, alreadyLanded: boolean): boolean {
  if (b.vy < 0) return false
  let landed = alreadyLanded
  let on = false
  for (const k of scene.ramps) {
    const r = world.track.ramps[k]!
    const s = rampSupportY(r, b.x, b.w)
    if (s === null || b.y > r.y + r.h) continue
    if (b.y >= s || (!landed && b.y >= s - world.cfg.rampSnapPx)) {
      b.y = s
      landed = true
      on = true
    }
  }
  return on
}

/**
 * A trap that would hit the body at `t` (the owner is safe during its grace), or a spike ball
 * that started ahead of the bot and is still in its way (moved `k + 1` steps on).
 */
export function trapAt(
  world: World,
  i: number,
  b: Sim,
  t: number,
  k: number,
): (Aabb & { id: number }) | null {
  for (const tr of world.traps) {
    if (t >= tr.expiresAtTick || (tr.owner === i && t < tr.ownerSafeUntil)) continue
    if (bodiesOverlap(b, tr)) return { x: tr.x, y: tr.y, w: tr.w, h: tr.h, id: tr.id }
  }
  const px = world.players[i]!.x
  for (const pr of world.projectiles) {
    if (pr.kind !== 'spike_ball' || pr.owner === i || pr.x < px || t >= pr.expiresAtTick) continue
    const ball = { x: pr.x + pr.vx * (k + 1), y: pr.y, w: pr.w, h: pr.h }
    if (bodiesOverlap(b, ball)) return { ...ball, id: pr.id }
  }
  return null
}
